import React, { useContext, useEffect, useCallback } from 'react';
import { Box, Drawer, AppBar, Toolbar, List, Typography, Divider, IconButton, ListItem, ListItemButton, ListItemIcon, ListItemText, Container, Button, Avatar, Chip, Badge, BottomNavigation, BottomNavigationAction } from '@mui/material';
import { Link, NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import withUser from '@components/withUser';
import type { WithUserProps } from '@components/withUser';
import LanguageDropdown from './LanguageDropdown';
import MenuIcon from '@mui/icons-material/Menu';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DashboardIcon from '@mui/icons-material/Dashboard';
import EventIcon from '@mui/icons-material/Event';
import PeopleIcon from '@mui/icons-material/People';
import SpaIcon from '@mui/icons-material/Spa';
import GroupsIcon from '@mui/icons-material/Groups';
import SettingsIcon from '@mui/icons-material/Settings';
import BusinessIcon from '@mui/icons-material/Business';
import ChatIcon from '@mui/icons-material/Chat';
import LogoutIcon from '@mui/icons-material/Logout';
import LanguageIcon from '@mui/icons-material/Language';
import ReportIcon from '@mui/icons-material/Report';
import RateReviewIcon from '@mui/icons-material/RateReview';
import WarningIcon from '@mui/icons-material/Warning';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { updateLanguage } from '@services/profileService';
import { getAllNotificationCount } from '@services/chatService';
import { CountContext } from '../context/CountContext';
import { useGodMode } from '../context/GodModeContext';
import { brand, gradient } from '../theme/tokens';

const drawerWidth = 248;

const getInitials = (name: string): string =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('') || '?';

interface MenuItem {
  text: string;
  icon: React.ReactNode;
  path?: string;
  action?: () => void;
}

type LayoutProps = WithUserProps;

const Layout: React.FC<LayoutProps> = ({ user }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const countContext = useContext(CountContext);
  const counts = countContext?.counts ?? { totalCount: 0 };
  const updateCount = countContext?.updateCount;
  const { isGodModeAvailable, isImpersonating, impersonatedUser, exitGodMode } = useGodMode();
  const [mobileOpen, setMobileOpen] = React.useState<boolean>(false);
  const [lastFetchTime, setLastFetchTime] = React.useState<number>(0);
  const FETCH_INTERVAL = 30000; // 30 seconds

  const stableUpdateCount = useCallback((key: string, value: number) => {
    if (updateCount) {
      updateCount(key, value);
    }
  }, [updateCount]);

  // Add new useEffect for notification counts
  useEffect(() => {
    let mounted = true;
    const fetchNotificationCount = async (): Promise<void> => {
      const now = Date.now();
      if (now - lastFetchTime < FETCH_INTERVAL) {
        return; // Skip if we've fetched recently
      }

      try {
        const response = await getAllNotificationCount();
        if (mounted && stableUpdateCount) {
          stableUpdateCount('totalCount', response.data.totalUnread);
          setLastFetchTime(now);
        }
      } catch (error) {
        console.error('Error fetching notification count:', error);
      }
    };

    // Initial fetch
    if (updateCount) {
      fetchNotificationCount();
    }

    // Set up polling
    const timeoutId = setInterval(fetchNotificationCount, FETCH_INTERVAL);

    return () => {
      mounted = false;
      if (timeoutId) {
        clearInterval(timeoutId);
      }
    };
  }, [stableUpdateCount, lastFetchTime, updateCount]);

  const handleDrawerToggle = (): void => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenuItemClick = (): void => {
    if (mobileOpen) {
      setMobileOpen(false);
    }
  };

  const changeLanguage = useCallback(async (lng: string): Promise<void> => {
    try {
      // First update the UI language
      await i18n.changeLanguage(lng);

      // Then update the backend
      await updateLanguage(lng);

      // Force a re-render of the menu items to update translations
      setMobileOpen(prev => !prev);
      setMobileOpen(prev => !prev);
    } catch (error) {
      console.error('Error changing language:', error);
      // Revert the language change if the backend update fails
      await i18n.changeLanguage(i18n.language);
    }
  }, [i18n]);

  const adminMenuItems: MenuItem[] = [
    { text: t('dashboard.dashboard'), icon: <DashboardIcon />, path: '/dashboard' },
    { text: t('dashboard.manageStylists'), icon: <PeopleIcon />, path: '/stylist' },
    { text: t('dashboard.manageBusinessProfile'), icon: <BusinessIcon />, path: '/business-profile' },
    { text: t('dashboard.managePlans'), icon: <SettingsIcon />, path: '/plans' },
    { text: t('dashboard.manageShowcases'), icon: <SpaIcon />, path: '/showcase-management' },
    { text: t('dashboard.manageReservations'), icon: <EventIcon />, path: '/reservations' },
    { text: t('dashboard.viewCreditLogs'), icon: <GroupsIcon />, path: '/credit-logs' },
    { text: t('dashboard.viewMonthlyReport'), icon: <ReportIcon />, path: '/monthly-report' },
    { text: t('dashboard.notifications'), icon: <ChatIcon />, path: '/chat-list' },
    { text: t('dashboard.reviews'), icon: <RateReviewIcon />, path: '/reviews' },
    { text: t('dashboard.logout'), icon: <LogoutIcon />, action: () => {
        // Clear impersonation data if any
        sessionStorage.removeItem('impersonation_token');
        sessionStorage.removeItem('impersonated_user');
        sessionStorage.removeItem('godmode_original_user');
        sessionStorage.removeItem('godmode_original_token');
        // Clear normal auth
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/';
      }
    },
  ];

  const stylistMenuItems: MenuItem[] = [
    { text: t('dashboard.dashboard'), icon: <DashboardIcon />, path: '/dashboard' },
    { text: t('dashboard.manageStylistProfile'), icon: <PeopleIcon />, path: '/stylist-profile' },
    { text: t('dashboard.managePlans'), icon: <SettingsIcon />, path: '/plans' },
    { text: t('dashboard.manageShowcases'), icon: <SpaIcon />, path: '/showcase-management' },
    { text: t('dashboard.manageReservations'), icon: <EventIcon />, path: '/reservations' },
    { text: t('dashboard.notifications'), icon: <ChatIcon />, path: '/chat-list' },
    { text: t('dashboard.reviews'), icon: <RateReviewIcon />, path: '/reviews' },
    { text: t('dashboard.logout'), icon: <LogoutIcon />, action: () => {
        // Clear impersonation data if any
        sessionStorage.removeItem('impersonation_token');
        sessionStorage.removeItem('impersonated_user');
        sessionStorage.removeItem('godmode_original_user');
        sessionStorage.removeItem('godmode_original_token');
        // Clear normal auth
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/';
      }
    },
  ];

  const menuItems: MenuItem[] = user.role === 'admin' || user.businessName ? adminMenuItems : stylistMenuItems;

  // Map paths to titles
  const pathToTitleMap: Record<string, string> = {
    '/dashboard': t('dashboard.dashboard'),
    '/stylist': t('dashboard.manageStylists'),
    '/business-profile': t('dashboard.manageBusinessProfile'),
    '/plans': t('dashboard.managePlans'),
    '/showcase-management': t('dashboard.manageShowcases'),
    '/reservations': t('dashboard.manageReservations'),
    '/transactions': t('dashboard.viewTransactions'),
    '/credit-logs': t('dashboard.viewCreditLogs'),
    '/chat-list': t('dashboard.notifications'),
    '/chat': t('dashboard.chat'),
    '/notification': t('dashboard.notifications'),
    '/stylist-profile': t('dashboard.manageStylistProfile'),
    '/payout-logs': t('dashboard.viewPayouts'),
    '/monthly-report': t('dashboard.viewMonthlyReport'),
    '/reviews': t('dashboard.reviews'),
  };

  // Determine the current title based on the path
  const firstPathSegment = location.pathname.split('/')[1];
  const currentTitle = pathToTitleMap[`/${firstPathSegment}`] || t('layout.title');

  // Compact primary destinations for the mobile bottom navigation bar.
  const bottomNavItems: { label: string; icon: React.ReactNode; path: string; badge?: number }[] = [
    { label: t('dashboard.dashboard'), icon: <DashboardIcon />, path: '/dashboard' },
    { label: t('dashboard.manageReservations'), icon: <EventIcon />, path: '/reservations' },
    { label: t('dashboard.manageShowcases'), icon: <SpaIcon />, path: '/showcase-management' },
    { label: t('dashboard.notifications'), icon: <ChatIcon />, path: '/chat-list', badge: counts.totalCount },
  ];
  const activeBottomPath =
    bottomNavItems.find((item) => location.pathname.startsWith(item.path))?.path ?? false;

  const displayName = user.businessName || user.displayName || user.email || t('layout.title');
  const avatarSrc = (user as { logo?: string }).logo || user.profileImage || undefined;
  const roleLabel =
    user.role === 'admin'
      ? 'Admin'
      : user.businessName
        ? t('dashboard.manageBusinessProfile', 'Business')
        : t('dashboard.stylist', 'Stylist');

  const drawer = (
    <Box sx={{ overflow: 'auto', height: '100%', background: gradient.ink, color: 'white', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ p: 2.5, pt: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 1 }}>
        <Avatar
          src={avatarSrc}
          sx={{ width: 64, height: 64, bgcolor: brand.gold, color: '#fff', fontWeight: 700, fontSize: '1.4rem', border: '2px solid rgba(255,255,255,0.25)' }}
        >
          {getInitials(displayName)}
        </Avatar>
        <Typography sx={{ fontWeight: 600, color: 'white', lineHeight: 1.2, maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }} noWrap>
          {displayName}
        </Typography>
        <Chip
          label={roleLabel}
          size="small"
          sx={{ bgcolor: 'rgba(199,164,110,0.25)', color: brand.goldLight, fontWeight: 500, height: 22 }}
        />
        {typeof user.credit === 'number' && (
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.75)' }}>
            {t('dashboard.credit', 'Credit')}: <b style={{ color: brand.goldLight }}>{user.credit}</b>
          </Typography>
        )}
      </Box>
      <Divider sx={{ backgroundColor: 'rgba(255,255,255,0.1)' }} />
      <List>
        {menuItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            {item.path ? (
              <ListItemButton
                component={NavLink}
                to={item.path}
                // Use isActive callback so '/reservations' doesn't auto-match '/reservations-v2'
                // (NavLink's default isActive does startsWith which causes the double-highlight bug).
                isActive={(_match, location) => location.pathname === item.path}
                selected={location.pathname === item.path}
                onClick={handleMenuItemClick}
                sx={{
                  '&.Mui-selected': {
                    backgroundColor: 'rgba(199, 164, 110, 0.2)',
                    '&:hover': {
                      backgroundColor: 'rgba(199, 164, 110, 0.3)',
                    },
                  },
                  '&:hover': {
                    backgroundColor: 'rgba(199, 164, 110, 0.1)',
                  },
                }}
              >
                <ListItemIcon sx={{ color: location.pathname === item.path ? 'var(--color-primary)' : 'white' }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {item.text}
                      {item.path === '/chat-list' && (counts.totalCount > 0) && (
                        <Box
                          className="notification-badge"
                          component="span"
                          sx={{
                            backgroundColor: 'red',
                            color: 'white',
                            borderRadius: '50%',
                            padding: '0.2em 0.5em',
                            fontSize: '0.75em',
                            marginLeft: '0.5em',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minWidth: '1.5em',
                            height: '1.5em',
                          }}
                        >
                          {counts.totalCount}
                        </Box>
                      )}
                    </Box>
                  }
                />
              </ListItemButton>
            ) : (
              <ListItemButton onClick={() => { item.action?.(); handleMenuItemClick(); }}>
                <ListItemIcon sx={{ color: 'white' }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText primary={item.text} />
              </ListItemButton>
            )}
          </ListItem>
        ))}
        <ListItem disablePadding>
          <ListItemButton>
            <ListItemIcon sx={{ color: 'white' }}>
              <LanguageIcon />
            </ListItemIcon>
            <LanguageDropdown currentLanguage={i18n.language} changeLanguage={changeLanguage} />
          </ListItemButton>
        </ListItem>
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      {/* Impersonation Warning Banner - only show if user has god mode privilege */}
      {isGodModeAvailable && isImpersonating && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: { sm: `${drawerWidth}px` },
            right: 0,
            backgroundColor: '#ff9800',
            color: '#000',
            py: 0.5,
            px: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            zIndex: 1201,
          }}
        >
          <WarningIcon fontSize="small" />
          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
            IMPERSONATING: {impersonatedUser?.displayName || impersonatedUser?.email} ({impersonatedUser?.role || 'user'})
          </Typography>
          <Button
            size="small"
            variant="outlined"
            onClick={exitGodMode}
            sx={{
              ml: 2,
              color: '#000',
              borderColor: '#000',
              '&:hover': {
                borderColor: '#000',
                backgroundColor: 'rgba(0,0,0,0.1)',
              },
            }}
          >
            Exit
          </Button>
        </Box>
      )}
      <AppBar position="fixed" sx={{ width: { sm: `calc(100% - ${drawerWidth}px)` }, ml: { sm: `${drawerWidth}px` }, top: (isGodModeAvailable && isImpersonating) ? 36 : 0 }}>
        <Toolbar>
          {!location.pathname.endsWith('/dashboard') && (
            <IconButton color="inherit" aria-label="back" edge="start" onClick={() => navigate(-1)} sx={{ mr: 2 }}>
              <ArrowBackIcon />
            </IconButton>
          )}
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            {currentTitle}
          </Typography>
          <IconButton color="inherit" aria-label="open drawer" edge="end" onClick={handleDrawerToggle} sx={{ display: { sm: 'none' } }}>
            <MenuIcon />
          </IconButton>
        </Toolbar>
      </AppBar>
      <Box component="nav" sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}>
        <Drawer
          variant="temporary"
          anchor="right"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{ display: { xs: 'block', sm: 'none' }, '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth } }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              background: 'linear-gradient(135deg, #232122 0%, #4D4A4B 100%)',
              color: 'white',
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          mt: (isGodModeAvailable && isImpersonating) ? '36px' : 0,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          minHeight: '100vh',
          bgcolor: 'background.default',
          pb: { xs: 'calc(72px + env(safe-area-inset-bottom))', sm: 0 },
          overflowX: 'hidden',
          maxWidth: '100vw',
        }}
      >
        <Toolbar />
        <Container maxWidth={false} disableGutters sx={{ px: 0 }}>
          <Outlet />
        </Container>
      </Box>

      {/* Mobile bottom navigation */}
      <BottomNavigation
        value={activeBottomPath}
        showLabels
        sx={{
          display: { xs: 'flex', sm: 'none' },
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: (theme) => theme.zIndex.appBar,
          height: 'auto',
          pt: 0.5,
          pb: 'env(safe-area-inset-bottom)',
          borderTop: `1px solid ${brand.border}`,
          boxShadow: '0 -2px 12px rgba(35,33,34,0.08)',
          '& .MuiBottomNavigationAction-root': {
            minWidth: 0,
            color: brand.inkSoft,
            py: 0.75,
            // keep labels on a single line so longer zh strings don't wrap on
            // narrow phones (e.g. 管理預約 / 管理作品集).
            '& .MuiBottomNavigationAction-label': {
              fontSize: 11,
              lineHeight: 1.2,
              whiteSpace: 'nowrap',
              letterSpacing: 0.2,
            },
          },
          '& .Mui-selected': { color: `${brand.goldDark} !important` },
        }}
      >
        {bottomNavItems.map((item) => (
          <BottomNavigationAction
            key={item.path}
            component={Link}
            to={item.path}
            value={item.path}
            label={item.label}
            icon={
              item.badge && item.badge > 0 ? (
                <Badge badgeContent={item.badge} color="error">
                  {item.icon}
                </Badge>
              ) : (
                item.icon
              )
            }
          />
        ))}
        <BottomNavigationAction
          label={t('layout.more', 'More')}
          value="more"
          onClick={handleDrawerToggle}
          icon={<MoreHorizIcon />}
        />
      </BottomNavigation>
    </Box>
  );
};

const WrappedLayout = withUser(Layout);
export default WrappedLayout;
// Mon Aug  3 06:14:13 AM UTC 2026
